/**
 * Cliente HTTP do front (mesma origem, opção gateway — ADR-006). GET simples;
 * mutações usam CSRF double-submit (RNF-S02): lê o cookie `csrf` (ou busca em
 * /api/csrf) e ecoa no header `x-csrf-token`. Sem cores/regras de negócio aqui —
 * só transporte.
 */

/** Lê o token CSRF do cookie `csrf` (legível por JS, double-submit). */
export function readCsrfCookie(cookie: string): string | null {
  const m = cookie.match(/(?:^|;\s*)csrf=([^;]+)/);
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

async function ensureCsrf(): Promise<string | null> {
  const fromCookie = readCsrfCookie(typeof document !== 'undefined' ? document.cookie : '');
  if (fromCookie) return fromCookie;
  try {
    const res = await fetch('/api/csrf', { credentials: 'same-origin' });
    if (res.ok) return ((await res.json()) as { token?: string }).token ?? null;
  } catch {
    /* mutação falhará com 403 e a UI mostra erro */
  }
  return readCsrfCookie(typeof document !== 'undefined' ? document.cookie : '');
}

/** GET JSON; retorna `null` em erro de rede ou status != ok. */
export async function getJson<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(path, { credentials: 'same-origin' });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export interface ApiResult<T> {
  ok: boolean;
  status: number;
  data: T | null;
}

/**
 * Mutação (POST/PATCH/DELETE) com CSRF; resposta JSON. `FormData` vai como multipart (o
 * navegador define o `content-type` com o boundary); qualquer outro corpo vai como JSON.
 * Não lança — devolve status.
 */
export async function mutateJson<T = unknown>(
  path: string,
  body: unknown,
  method: 'POST' | 'PATCH' | 'DELETE' = 'POST',
): Promise<ApiResult<T>> {
  const token = await ensureCsrf();
  try {
    const res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: {
        ...(body instanceof FormData ? {} : { 'content-type': 'application/json' }),
        ...(token ? { 'x-csrf-token': token } : {}),
      },
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
    let data: T | null = null;
    try {
      data = (await res.json()) as T;
    } catch {
      data = null;
    }
    return { ok: res.ok, status: res.status, data };
  } catch {
    return { ok: false, status: 0, data: null };
  }
}

/** Login é isento de CSRF (bootstrap pré-sessão), mas mesma origem. */
export async function login(
  email: string,
  password: string,
): Promise<ApiResult<{ user: unknown }>> {
  try {
    const res = await fetch('/api/login', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    let data: { user: unknown } | null = null;
    try {
      data = (await res.json()) as { user: unknown };
    } catch {
      data = null;
    }
    return { ok: res.ok, status: res.status, data };
  } catch {
    return { ok: false, status: 0, data: null };
  }
}

/** POST pré-sessão sem CSRF (recuperação de senha, D1/ADR-007). Não lança. */
async function postNoCsrf<T = unknown>(path: string, body: unknown): Promise<ApiResult<T>> {
  try {
    const res = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    let data: T | null = null;
    try {
      data = (await res.json()) as T;
    } catch {
      data = null;
    }
    return { ok: res.ok, status: res.status, data };
  } catch {
    return { ok: false, status: 0, data: null };
  }
}

/** Solicita o link de recuperação por e-mail (autosserviço). Resposta sempre neutra. */
export function requestPasswordReset(email: string): Promise<ApiResult<{ ok: boolean }>> {
  return postNoCsrf('/api/auth/recuperar', { email });
}

/** Redefine a senha a partir do token recebido no e-mail (uso único). */
export function resetPassword(
  token: string,
  senhaNova: string,
): Promise<ApiResult<{ ok: boolean }>> {
  return postNoCsrf('/api/auth/redefinir', { token, senhaNova });
}

/** Lê o token de redefinição da query string (?token=...). `null` se ausente. */
export function readResetTokenFromUrl(search: string): string | null {
  const m = search.match(/[?&]token=([^&]+)/);
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

/** Lê a preferência de opt-out de e-mail do usuário. */
export async function getEmailOptOut(): Promise<boolean> {
  const data = await getJson<{ emailOptOut: boolean }>('/api/me/preferencias');
  return data?.emailOptOut ?? false;
}

/** Define a preferência de opt-out de e-mail (CSRF). */
export function setEmailOptOut(optOut: boolean): Promise<ApiResult<{ ok: boolean }>> {
  return mutateJson('/api/me/preferencias', { emailOptOut: optOut });
}
